import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    ClipboardList, Plus, ArrowLeft, ArrowRight, Loader2,
    CheckCircle2, Mail, Clock, X, Search, UserRound, Link as LinkIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Assessments Hub — all assessments in one place.
 *
 * Coach lands here from the dashboard side nav. Top button starts a fresh
 * assessment (assign to an existing profile via search, OR type parent +
 * kid names for a brand-new walk-in → straight into the runner). Below,
 * the table lists every past session with date / parent / kids / coach
 * and a one-tap "resend recap" if needed.
 *
 * Designed for tablet: button-first, no scrolling required for the most
 * common action (start new).
 */
export default function AssessmentsHub() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [busy, setBusy] = useState(true);
    const [showStart, setShowStart] = useState(false);
    const [resendingId, setResendingId] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user || !["owner", "coach"].includes(user.role)) {
            navigate("/dashboard", { replace: true });
        }
    }, [loading, user, navigate]);

    const refresh = async () => {
        setBusy(true);
        try {
            const [d, c] = await Promise.all([
                axios.get(`${API}/coach/assessment-sessions?status=draft`, { withCredentials: true }),
                axios.get(`${API}/coach/assessment-sessions?status=completed`, { withCredentials: true }),
            ]);
            setDrafts(d.data || []);
            setCompleted(c.data || []);
        } catch (e) {
            toast.error("Couldn't load assessments", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const handleResend = async (sessionId) => {
        if (!window.confirm("Resend the assessment recap email to the parent now?")) return;
        setResendingId(sessionId);
        try {
            const r = await axios.post(
                `${API}/coach/assessment-sessions/${sessionId}/resend-recap`,
                {},
                { withCredentials: true },
            );
            const n = r.data?.sent?.length || 0;
            toast.success("Recap resent", {
                description: n === 1 ? "Email delivered to parent." : `${n} emails sent.`,
            });
        } catch (e) {
            toast.error("Couldn't resend", { description: e?.response?.data?.detail });
        } finally {
            setResendingId(null);
        }
    };

    if (loading || busy) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <Link
                        to="/coach/dashboard"
                        data-testid="assessments-back"
                        className="text-zinc-400 hover:text-white inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="w-4 h-4" /> Dashboard
                    </Link>
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-3">
                    <span className="inline-block w-10 h-px bg-brand-red" />
                    Coach console · Assessments
                </div>
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <h1 className="font-display text-5xl md:text-6xl tracking-wider leading-[0.92] text-white">
                            ASSESSMENT <span className="text-brand-red">HUB.</span>
                        </h1>
                        <p className="mt-3 text-zinc-300 max-w-2xl text-base">
                            Every assessment in one place. Start a new one in two taps, or resend any past recap to a parent who lost the email.
                        </p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <button
                            type="button"
                            data-testid="assessments-copy-link"
                            onClick={() => {
                                const url = `${window.location.origin}/coach/assessments`;
                                navigator.clipboard.writeText(url).then(
                                    () => toast.success("Link copied", { description: "Send it to any coach — they'll need to be signed in with their coach account." }),
                                    () => toast.error("Couldn't copy", { description: url }),
                                );
                            }}
                            className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-zinc-700 hover:border-brand-red text-zinc-200 hover:text-white px-4 py-3 transition-colors"
                        >
                            <LinkIcon className="w-4 h-4" /> Copy link
                        </button>
                        <button
                            type="button"
                            data-testid="assessments-start-new"
                            onClick={() => setShowStart(true)}
                            className="btn-tactical inline-flex items-center gap-2 text-base px-6 py-3"
                        >
                            <Plus className="w-5 h-5" /> Start new assessment
                        </button>
                    </div>
                </div>

                {/* In-progress drafts */}
                {drafts.length > 0 && (
                    <section className="mb-12">
                        <h2 className="text-xs tracking-[0.3em] uppercase text-amber-300 mb-3 inline-flex items-center gap-2">
                            <Clock className="w-4 h-4" /> In progress · {drafts.length}
                        </h2>
                        <div className="border border-amber-900/40 bg-amber-950/10 rounded-2xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-amber-950/30 text-[10px] tracking-wider uppercase text-amber-200">
                                    <tr>
                                        <th className="text-left px-4 py-3">Started</th>
                                        <th className="text-left px-4 py-3">Parent</th>
                                        <th className="text-left px-4 py-3">Athlete(s)</th>
                                        <th className="text-left px-4 py-3">Coach</th>
                                        <th className="text-right px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drafts.map((s) => (
                                        <DraftRow key={s.session_id} session={s} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {/* Completed history */}
                <section>
                    <h2 className="text-xs tracking-[0.3em] uppercase text-zinc-400 mb-3 inline-flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" /> Completed · {completed.length}
                    </h2>
                    {completed.length === 0 ? (
                        <div className="border border-zinc-900 bg-zinc-950/40 rounded-2xl px-6 py-12 text-center text-zinc-500">
                            No assessments yet. Click <span className="text-white">+ Start new assessment</span> to run your first.
                        </div>
                    ) : (
                        <div className="border border-zinc-900 bg-zinc-950/40 rounded-2xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-900/50 text-[10px] tracking-wider uppercase text-zinc-300">
                                    <tr>
                                        <th className="text-left px-4 py-3">Date</th>
                                        <th className="text-left px-4 py-3">Parent</th>
                                        <th className="text-left px-4 py-3">Athlete(s)</th>
                                        <th className="text-left px-4 py-3">Coach</th>
                                        <th className="text-right px-4 py-3 w-32">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {completed.map((s) => (
                                        <CompletedRow
                                            key={s.session_id}
                                            session={s}
                                            onResend={handleResend}
                                            resending={resendingId === s.session_id}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>

            {showStart && (
                <QuickStartModal
                    coachName={user?.name || user?.email || ""}
                    onClose={() => setShowStart(false)}
                    onStarted={(sessionId) => {
                        setShowStart(false);
                        navigate(`/coach/assessment/${sessionId}`);
                    }}
                />
            )}
        </div>
    );
}


function DraftRow({ session }) {
    const startedDate = session.started_at ? new Date(session.started_at) : null;
    const participantNames = (session.participants || []).map((p) => p.name).filter(Boolean).join(", ");
    const parentName = session.quickstart_parent_name || "—";
    return (
        <tr data-testid={`assessment-draft-${session.session_id}`} className="border-t border-amber-900/30">
            <td className="px-4 py-3 text-zinc-200 whitespace-nowrap">
                {startedDate ? startedDate.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
            </td>
            <td className="px-4 py-3 text-white">{parentName}</td>
            <td className="px-4 py-3 text-zinc-200">{participantNames || "—"}</td>
            <td className="px-4 py-3 text-zinc-300">{session.started_by_name || "—"}</td>
            <td className="px-4 py-3 text-right">
                <Link
                    to={`/coach/assessment/${session.session_id}`}
                    data-testid={`assessment-resume-${session.session_id}`}
                    className="inline-flex items-center gap-1.5 text-amber-200 hover:text-white text-xs tracking-wider uppercase"
                >
                    Resume <ArrowRight className="w-3 h-3" />
                </Link>
            </td>
        </tr>
    );
}


function CompletedRow({ session, onResend, resending }) {
    const completedDate = session.completed_at ? new Date(session.completed_at) : null;
    const participantNames = (session.participants || []).map((p) => p.name).filter(Boolean).join(", ");
    const parentName = session.quickstart_parent_name || "—";
    return (
        <tr data-testid={`assessment-completed-${session.session_id}`} className="border-t border-zinc-900">
            <td className="px-4 py-3 text-zinc-200 whitespace-nowrap">
                {completedDate ? completedDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
            </td>
            <td className="px-4 py-3 text-white">{parentName}</td>
            <td className="px-4 py-3 text-zinc-200">{participantNames || "—"}</td>
            <td className="px-4 py-3 text-zinc-300">{session.started_by_name || "—"}</td>
            <td className="px-4 py-3 text-right">
                <button
                    type="button"
                    data-testid={`assessment-resend-${session.session_id}`}
                    onClick={() => onResend(session.session_id)}
                    disabled={resending}
                    className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white text-xs tracking-wider uppercase border border-zinc-700 hover:border-brand-red px-3 py-1.5 transition-colors disabled:opacity-50"
                >
                    {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                    {resending ? "Sending…" : "Resend"}
                </button>
            </td>
        </tr>
    );
}


function QuickStartModal({ coachName, onClose, onStarted }) {
    // Two ways in: assign to an EXISTING roster profile (search-as-you-type)
    // or the original walk-in flow that creates new parent + kid records.
    const [mode, setMode] = useState("existing");
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
            data-testid="assessments-quickstart-modal"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 w-full max-w-2xl shadow-2xl my-8"
            >
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                    <ClipboardList className="w-3.5 h-3.5" /> New assessment
                </div>
                <div className="flex gap-2 mb-6">
                    <button
                        type="button"
                        data-testid="assess-mode-existing"
                        onClick={() => setMode("existing")}
                        className={`text-[10px] tracking-[0.2em] uppercase border px-4 py-2.5 rounded-lg transition-colors ${mode === "existing" ? "border-brand-red text-brand-red bg-brand-red/5" : "border-zinc-800 text-zinc-300 hover:text-white"}`}
                    >
                        Existing profile
                    </button>
                    <button
                        type="button"
                        data-testid="assess-mode-new"
                        onClick={() => setMode("new")}
                        className={`text-[10px] tracking-[0.2em] uppercase border px-4 py-2.5 rounded-lg transition-colors ${mode === "new" ? "border-brand-red text-brand-red bg-brand-red/5" : "border-zinc-800 text-zinc-300 hover:text-white"}`}
                    >
                        New walk-in
                    </button>
                </div>
                {mode === "existing" ? (
                    <AssignExistingForm coachName={coachName} onClose={onClose} onStarted={onStarted} />
                ) : (
                    <WalkInForm coachName={coachName} onClose={onClose} onStarted={onStarted} />
                )}
            </div>
        </div>
    );
}


const STATUS_CHIP = {
    lead: "border-amber-500/40 text-amber-300",
    member: "border-emerald-500/40 text-emerald-300",
    non_member: "border-sky-500/40 text-sky-300",
    inactive: "border-zinc-700 text-zinc-400",
    mnp: "border-zinc-700 text-zinc-400",
};


function AssignExistingForm({ coachName, onClose, onStarted }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(null);
    const [checked, setChecked] = useState({});
    const [showNewAthlete, setShowNewAthlete] = useState(false);
    const [newFirst, setNewFirst] = useState("");
    const [newLast, setNewLast] = useState("");
    const [coachOverride, setCoachOverride] = useState(coachName || "");
    const [submitting, setSubmitting] = useState(false);

    // Debounced typeahead
    useEffect(() => {
        if (selected) return undefined;
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            return undefined;
        }
        const t = setTimeout(async () => {
            setSearching(true);
            try {
                const r = await axios.get(`${API}/coach/clients/typeahead`, { params: { q }, withCredentials: true });
                setResults(r.data?.results || []);
            } catch {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [query, selected]);

    const pick = (p) => {
        setSelected(p);
        setResults([]);
        const init = {};
        (p.athletes || []).forEach((a) => { init[a.athlete_id] = true; });
        setChecked(init);
        if ((p.athletes || []).length === 0) {
            // Adult/self profile — prefill so a self-assessment is one tap.
            const parts = (p.name || "").split(" ");
            setNewFirst(parts[0] || "");
            setNewLast(parts.slice(1).join(" "));
            setShowNewAthlete(true);
        } else {
            setShowNewAthlete(false);
            setNewFirst("");
            setNewLast("");
        }
    };

    const clearSelection = () => {
        setSelected(null);
        setChecked({});
        setShowNewAthlete(false);
        setNewFirst("");
        setNewLast("");
    };

    const pickedIds = selected ? Object.keys(checked).filter((k) => checked[k]) : [];
    const hasNew = showNewAthlete && newFirst.trim().length > 0;
    const canSubmit = !!selected && (pickedIds.length > 0 || hasNew) && coachOverride.trim().length > 0 && !submitting;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const r = await axios.post(
                `${API}/coach/assessment-sessions/assign`,
                {
                    user_id: selected.user_id,
                    athlete_ids: pickedIds,
                    new_athletes: hasNew ? [{ first_name: newFirst.trim(), last_name: newLast.trim() }] : [],
                    coach_name_override: coachOverride.trim(),
                },
                { withCredentials: true },
            );
            toast.success("Assessment assigned", {
                description: r.data?.recap_email
                    ? `Recap will be emailed to ${r.data.recap_email}.`
                    : "No email on this profile — add one later to send the recap.",
            });
            onStarted(r.data.session_id);
        } catch (err) {
            toast.error("Couldn't assign", { description: err?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit}>
            <h2 className="font-display text-2xl tracking-wider text-white mb-1">Who&apos;s it for?</h2>
            <p className="text-zinc-400 text-sm mb-5">
                Type a name — leads, members, and non-members all show up. Pick the profile and we&apos;ll attach the assessment (recap email autofills from their profile).
            </p>

            {!selected ? (
                <div className="relative">
                    <div className="relative">
                        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            data-testid="assign-search-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by parent, kid, or email…"
                            autoFocus
                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-base pl-10 pr-3 py-3 rounded-lg focus:outline-none focus:border-brand-red"
                        />
                        {searching && <Loader2 className="w-4 h-4 text-zinc-500 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
                    </div>
                    {results.length > 0 && (
                        <div data-testid="assign-search-results" className="mt-2 border border-zinc-800 rounded-lg overflow-hidden divide-y divide-zinc-900 max-h-72 overflow-y-auto">
                            {results.map((p) => (
                                <button
                                    key={p.user_id}
                                    type="button"
                                    data-testid={`assign-search-result-${p.user_id}`}
                                    onClick={() => pick(p)}
                                    className="w-full text-left px-4 py-3 bg-zinc-950 hover:bg-zinc-900 transition-colors"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-white text-sm font-medium">{p.name}</div>
                                        <span className={`text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 rounded ${STATUS_CHIP[p.status] || STATUS_CHIP.lead}`}>
                                            {(p.status || "lead").replace("_", "-")}
                                        </span>
                                    </div>
                                    <div className="text-zinc-500 text-xs mt-0.5">
                                        {p.email || "no email"}
                                        {(p.athletes || []).length > 0 && (
                                            <span className="text-zinc-400"> · {p.athletes.map((a) => a.name).join(", ")}</span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                    {query.trim().length >= 2 && !searching && results.length === 0 && (
                        <div className="mt-2 text-zinc-500 text-sm px-1">
                            No matches. Try the <span className="text-white">New walk-in</span> tab to create them.
                        </div>
                    )}
                </div>
            ) : (
                <div data-testid="assign-selected-profile" className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/50">
                    <div className="flex items-center justify-between gap-3 mb-1">
                        <div className="inline-flex items-center gap-2">
                            <UserRound className="w-4 h-4 text-brand-red" />
                            <span className="text-white font-medium">{selected.name}</span>
                            <span className={`text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 rounded ${STATUS_CHIP[selected.status] || STATUS_CHIP.lead}`}>
                                {(selected.status || "lead").replace("_", "-")}
                            </span>
                        </div>
                        <button
                            type="button"
                            data-testid="assign-change-profile"
                            onClick={clearSelection}
                            className="text-zinc-400 hover:text-white text-[10px] tracking-[0.2em] uppercase"
                        >
                            Change
                        </button>
                    </div>
                    <div className="text-xs mb-4 inline-flex items-center gap-1.5">
                        <Mail className="w-3 h-3 text-zinc-500" />
                        {selected.email ? (
                            <span className="text-zinc-300">Recap email → <span className="text-white" data-testid="assign-recap-email">{selected.email}</span></span>
                        ) : (
                            <span className="text-amber-300">No email on file — recap can&apos;t be emailed yet.</span>
                        )}
                    </div>

                    {(selected.athletes || []).length > 0 && (
                        <div className="space-y-2 mb-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">Assessment is for</div>
                            {selected.athletes.map((a) => (
                                <label key={a.athlete_id} className="flex items-center gap-2.5 text-sm text-zinc-200 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        data-testid={`assign-athlete-check-${a.athlete_id}`}
                                        checked={!!checked[a.athlete_id]}
                                        onChange={(e) => setChecked((prev) => ({ ...prev, [a.athlete_id]: e.target.checked }))}
                                        className="rounded border-zinc-600 bg-zinc-900 text-brand-red focus:ring-brand-red"
                                    />
                                    {a.name || a.athlete_id}
                                </label>
                            ))}
                        </div>
                    )}

                    {showNewAthlete ? (
                        <div className="border-t border-dashed border-zinc-900 pt-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2">
                                {(selected.athletes || []).length === 0 ? "Add the athlete being assessed" : "New athlete on this profile"}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    data-testid="assign-new-athlete-first"
                                    value={newFirst}
                                    onChange={(e) => setNewFirst(e.target.value)}
                                    placeholder="First name *"
                                    className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                                />
                                <input
                                    type="text"
                                    data-testid="assign-new-athlete-last"
                                    value={newLast}
                                    onChange={(e) => setNewLast(e.target.value)}
                                    placeholder="Last name"
                                    className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                                />
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            data-testid="assign-add-new-athlete"
                            onClick={() => setShowNewAthlete(true)}
                            className="text-zinc-400 hover:text-white text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                        >
                            <Plus className="w-3 h-3" /> Add a new athlete instead
                        </button>
                    )}
                </div>
            )}

            <div className="mt-6 border-t border-zinc-900 pt-5">
                <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Coach running this *</label>
                <input
                    type="text"
                    data-testid="assign-coach-name"
                    value={coachOverride}
                    onChange={(e) => setCoachOverride(e.target.value)}
                    required
                    className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                />
            </div>

            <div className="mt-8 flex items-center justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="assign-cancel"
                    className="text-zinc-400 hover:text-white text-xs tracking-wider uppercase px-3 py-2"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    data-testid="assign-submit"
                    disabled={!canSubmit}
                    className="btn-tactical inline-flex items-center gap-2 disabled:opacity-50"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    {submitting ? "Assigning…" : "Start assessment"}
                </button>
            </div>
        </form>
    );
}


function WalkInForm({ coachName, onClose, onStarted }) {
    // Each row = one (parent, kid) pair. Siblings share a parent_email →
    // collapse to one parent server-side. Friends have different emails →
    // each get their own parent + recap email.
    const blankRow = () => ({
        parentFirst: "",
        parentLast: "",
        parentEmail: "",
        kidFirst: "",
        kidLast: "",
        sameAsAbove: false,
    });
    const [rows, setRows] = useState([blankRow()]);
    const [coachOverride, setCoachOverride] = useState(coachName || "");
    const [submitting, setSubmitting] = useState(false);

    const updateRow = (idx, patch) => {
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };

    const toggleSameAsAbove = (idx, on) => {
        setRows((prev) => prev.map((r, i) => {
            if (i !== idx) return r;
            if (on) {
                // Inherit parent fields from the previous row at submit time;
                // here we just track the toggle and clear visible parent inputs.
                return { ...r, sameAsAbove: true, parentFirst: "", parentLast: "", parentEmail: "" };
            }
            return { ...r, sameAsAbove: false };
        }));
    };

    const addRow = () => setRows((prev) => [...prev, blankRow()]);
    const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

    // Resolve each row's effective parent fields by walking up to the most
    // recent ancestor where `sameAsAbove` is false.
    const resolveRow = (idx) => {
        let row = rows[idx];
        let walk = idx;
        while (rows[walk]?.sameAsAbove && walk > 0) walk -= 1;
        const source = rows[walk] || row;
        return {
            parent_first_name: source.parentFirst.trim(),
            parent_last_name: source.parentLast.trim(),
            parent_email: source.parentEmail.trim(),
            kid_first_name: row.kidFirst.trim(),
            kid_last_name: row.kidLast.trim(),
        };
    };

    const canSubmit = useMemo(() => {
        if (submitting || coachOverride.trim().length === 0) return false;
        return rows.every((r, i) => {
            if (r.kidFirst.trim().length === 0) return false;
            // First row (or any row that's NOT "same as above") must have parent first name
            if (i === 0 || !r.sameAsAbove) {
                return r.parentFirst.trim().length > 0;
            }
            return true;
        });
    }, [rows, coachOverride, submitting]);

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const participants = rows.map((_, i) => resolveRow(i));
            const r = await axios.post(
                `${API}/coach/assessment-sessions/quick-start`,
                {
                    participants,
                    coach_name_override: coachOverride.trim(),
                },
                { withCredentials: true },
            );
            const n = r.data?.participant_count || 1;
            const families = r.data?.distinct_parents || 1;
            toast.success("Assessment started", {
                description: families === 1
                    ? `Running for ${n} athlete${n === 1 ? "" : "s"}.`
                    : `${n} athletes across ${families} families.`,
            });
            onStarted(r.data.session_id);
        } catch (e) {
            toast.error("Couldn't start", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit}>
            <h2 className="font-display text-2xl tracking-wider text-white mb-1">
                Who&apos;s here?
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
                Add each kid. Siblings: tick <span className="text-white">&quot;same parent as above&quot;</span>. Friend tagging along: leave it off and type their parent.
            </p>

            <div className="space-y-5">
                {rows.map((row, idx) => (
                    <ParticipantRowFields
                        key={idx}
                        idx={idx}
                        row={row}
                        canRemove={rows.length > 1}
                        onChange={(patch) => updateRow(idx, patch)}
                        onToggleSameAsAbove={(on) => toggleSameAsAbove(idx, on)}
                        onRemove={() => removeRow(idx)}
                    />
                ))}

                <button
                    type="button"
                    data-testid="quickstart-add-row"
                    onClick={addRow}
                    className="w-full border border-dashed border-zinc-700 hover:border-brand-red text-zinc-300 hover:text-white text-xs tracking-[0.2em] uppercase py-3 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                >
                    <Plus className="w-4 h-4" /> Add another kid
                </button>
            </div>

            <div className="mt-6 border-t border-zinc-900 pt-5">
                <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Coach running this *</label>
                <input
                    type="text"
                    data-testid="quickstart-coach-name"
                    value={coachOverride}
                    onChange={(e) => setCoachOverride(e.target.value)}
                    required
                    className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                />
            </div>

            <div className="mt-8 flex items-center justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="quickstart-cancel"
                    className="text-zinc-400 hover:text-white text-xs tracking-wider uppercase px-3 py-2"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    data-testid="quickstart-submit"
                    disabled={!canSubmit}
                    className="btn-tactical inline-flex items-center gap-2 disabled:opacity-50"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    {submitting ? "Starting…" : `Start assessment${rows.length > 1 ? ` (${rows.length})` : ""}`}
                </button>
            </div>
        </form>
    );
}


function ParticipantRowFields({ idx, row, canRemove, onChange, onToggleSameAsAbove, onRemove }) {
    const showParentFields = idx === 0 || !row.sameAsAbove;
    return (
        <div className="border border-zinc-900 rounded-xl p-4 bg-zinc-950/50">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">
                    Athlete {idx + 1}
                </div>
                <div className="flex items-center gap-3">
                    {idx > 0 && (
                        <label className="text-[11px] text-zinc-300 inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid={`quickstart-same-parent-${idx}`}
                                checked={row.sameAsAbove}
                                onChange={(e) => onToggleSameAsAbove(e.target.checked)}
                                className="rounded border-zinc-600 bg-zinc-900 text-brand-red focus:ring-brand-red"
                            />
                            Same parent as above
                        </label>
                    )}
                    {canRemove && (
                        <button
                            type="button"
                            data-testid={`quickstart-remove-${idx}`}
                            onClick={onRemove}
                            className="text-zinc-500 hover:text-rose-400 p-1 -m-1"
                            title="Remove this athlete"
                            aria-label="Remove athlete"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {showParentFields && (
                <div className="space-y-3 mb-4 pb-4 border-b border-dashed border-zinc-900">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Parent first name *</label>
                            <input
                                type="text"
                                data-testid={`quickstart-parent-first-${idx}`}
                                value={row.parentFirst}
                                onChange={(e) => onChange({ parentFirst: e.target.value })}
                                required={showParentFields}
                                autoFocus={idx === 0}
                                className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Parent last name</label>
                            <input
                                type="text"
                                data-testid={`quickstart-parent-last-${idx}`}
                                value={row.parentLast}
                                onChange={(e) => onChange({ parentLast: e.target.value })}
                                className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">
                            Parent email <span className="text-zinc-600 normal-case tracking-normal text-[10px]">— required to email recap</span>
                        </label>
                        <input
                            type="email"
                            data-testid={`quickstart-parent-email-${idx}`}
                            value={row.parentEmail}
                            onChange={(e) => onChange({ parentEmail: e.target.value })}
                            placeholder="parent@example.com"
                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                        />
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Kid first name *</label>
                    <input
                        type="text"
                        data-testid={`quickstart-kid-first-${idx}`}
                        value={row.kidFirst}
                        onChange={(e) => onChange({ kidFirst: e.target.value })}
                        required
                        className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                    />
                </div>
                <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Kid last name</label>
                    <input
                        type="text"
                        data-testid={`quickstart-kid-last-${idx}`}
                        value={row.kidLast}
                        onChange={(e) => onChange({ kidLast: e.target.value })}
                        className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                    />
                </div>
            </div>
        </div>
    );
}
